import ReactDOM from "react-dom";
import React, { useCallback, useEffect, useState } from "react";
import ReactJson from "react-json-view";
import classnames from "classnames";
import * as urls from "../../../api/urls";
import { APIClient } from "api/apiCore";
import { Utils } from "helpers/utils";
import Loader from "components/Loader";
import { backdropStyle, modalStyle } from "DialogProviderShare";

interface HookQueueRow {
  queueID: number;
  hookUrl?: string | null;
  queueDate?: string | null;
  isProcessing?: boolean | null;
  pk?: string | null;
}

interface HookLogRow {
  entryId: number;
  queueID?: number | null;
  hookUrl?: string | null;
  queueDate?: string | null;
  processDateTime?: string | null;
  isError?: boolean | null;
  pk?: string | null;
}

interface HookHistoryDetail {
  variables?: string | null;
  hookParam?: string | null;
  result?: string | null;
  error?: string | null;
  isError?: boolean | null;
}

type Picked = { kind: "queue" | "log"; id: number };

/** "2026-10-12T10:20:30.123" -> "2026-10-12 10:20:30". The API sends server-local time without a timezone. */
const fmtDate = (v?: string | null) => (v ? String(v).replace("T", " ").substring(0, 19) : "");

/**
 * Parses a stored JSON string for the viewer. Values that are themselves JSON text - approveFlowJson and
 * tagMemberJson are sent as strings - are expanded too, so they can be browsed instead of read as one long line.
 * Returns null when the text isn't a JSON object/array, so the caller can show it as plain text.
 */
const parseForView = (text?: string | null): object | null => {
  if (!text) return null;
  const tryParse = (s: string): any => {
    const t = s.trim();
    if (!(t.startsWith("{") || t.startsWith("["))) return undefined;
    try { return JSON.parse(t); } catch { return undefined; }
  };
  const expand = (v: any): any => {
    if (typeof v === "string") {
      const p = tryParse(v);
      return p === undefined ? v : expand(p);
    }
    if (Array.isArray(v)) return v.map(expand);
    if (v && typeof v === "object") {
      const o: any = {};
      Object.keys(v).forEach(k => (o[k] = expand(v[k])));
      return o;
    }
    return v;
  };
  const root = tryParse(text);
  return root !== undefined && root !== null && typeof root === "object" ? expand(root) : null;
};

/** JSON viewer when the text is JSON, plain text otherwise. */
function TextOrJson({ text, isDark, danger }: { text?: string | null; isDark: boolean; danger?: boolean }) {
  if (!text) return <div className="text-muted fst-italic">(ว่าง)</div>;
  const obj = parseForView(text);
  if (obj) {
    return (
      <ReactJson
        src={obj}
        name={false}
        theme={isDark ? "monokai" : "rjv-default"}
        collapsed={2}
        displayDataTypes={false}
        enableClipboard={true}
        style={{ fontSize: 12, padding: 6, borderRadius: 4, wordBreak: "break-all" }}
      />
    );
  }
  return (
    <pre className={classnames("mb-0", { "text-danger": danger })}
      style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>{text}</pre>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <div className="fw-bold mb-1" style={{ fontSize: 13 }}>{title}</div>
      {children}
    </div>
  );
}

/**
 * Queue + history of one hook, opened from the "Hooked" column in Hook Manager.
 * Left: tb_hookqueue (top, usually empty) and tb_hooklog (bottom). Right: details of the clicked row,
 * loaded on click so the lists stay light.
 */
export default function HookHistoryDialog({ hookId, title, onClose }: { hookId: number; title?: string | null; onClose: () => void }) {
  const isDark = Utils.IsDarkTheme();
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState<HookQueueRow[]>([]);
  const [log, setLog] = useState<HookLogRow[]>([]);
  const [maxRows, setMaxRows] = useState(0);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [detail, setDetail] = useState<HookHistoryDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [pkFilter, setPkFilter] = useState("");
  const [performing, setPerforming] = useState(false);

  // pkFilter is read when load runs (Enter / ค้นหา / Refresh), not on every keystroke.
  const pkRef = React.useRef(pkFilter);
  pkRef.current = pkFilter;

  const load = useCallback(() => {
    setLoading(true);
    new APIClient().fnPostForm(urls.HOOK_HISTORY, { hookId, pk: pkRef.current.trim() })
      .then(rs => {
        if (rs?.success) {
          setQueue(rs.data?.queue ?? []);
          setLog(rs.data?.log ?? []);
          setMaxRows(rs.data?.maxRows ?? 0);
        } else {
          alert(rs?.message);
        }
        setLoading(false);
      })
      .catch(ex => {
        Utils.HandleException(ex, true);
        setLoading(false);
      });
  }, [hookId]);

  useEffect(() => { load(); }, [load]);

  const pick = (p: Picked) => {
    setPicked(p);
    setDetail(null);
    setDetailError(null);
    setLoadingDetail(true);
    new APIClient().fnPostForm(urls.HOOK_HISTORY_DETAIL, { hookId, kind: p.kind, id: p.id })
      .then(rs => {
        if (rs?.success) setDetail(rs.data);
        else setDetailError(rs?.message ?? "Error");
        setLoadingDetail(false);
      })
      .catch(ex => {
        Utils.HandleException(ex, true);
        setLoadingDetail(false);
      });
  };

  /**
   * Queue row: sends it now (same as the background worker) - it moves to History.
   * Log row: sends the same variables again with the hook's current settings - a new History row is added.
   * Either way the lists reload and the new History row is selected.
   */
  const perform = () => {
    if (!picked) return;
    const msg = picked.kind === "queue"
      ? "ส่งคิวนี้ไปยังปลายทางเดี๋ยวนี้?"
      : "ส่ง Hook นี้ซ้ำอีกครั้ง (ใช้ตัวแปรเดิม กับการตั้งค่า Hook ปัจจุบัน)?\nปลายทางจะได้รับข้อมูลซ้ำ";
    if (!window.confirm(msg)) return;
    setPerforming(true);
    new APIClient().fnPostForm(urls.HOOK_HISTORY_PERFORM, { hookId, kind: picked.kind, id: picked.id })
      .then(rs => {
        setPerforming(false);
        if (!rs?.success) { alert(rs?.message); return; }
        load();
        if (rs.data?.entryId) pick({ kind: "log", id: rs.data.entryId });
      })
      .catch(ex => {
        Utils.HandleException(ex, true);
        setPerforming(false);
      });
  };

  // Escape closes the popup. Registered on window like the other dialogs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isPicked = (kind: Picked["kind"], id: number) => picked?.kind === kind && picked.id === id;
  const pickedStyle: React.CSSProperties = { background: "var(--bs-primary)", color: "#fff" };

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      <div style={{ ...modalStyle, maxWidth: 1280, height: "88vh", display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Hook #{hookId} — Queue &amp; History</h5>
            {title && <div className="text-muted" style={{ fontSize: 12 }}>{title}</div>}
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <input
              className="form-control form-control-sm"
              style={{ width: 300 }}
              placeholder="กรอง PK (Task Id) แล้วกด Enter"
              value={pkFilter}
              onChange={e => setPkFilter(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") load(); }}
            />
            {pkFilter && (
              <button className="btn btn-outline-secondary btn-sm" title="ล้างตัวกรอง"
                onClick={() => { pkRef.current = ""; setPkFilter(""); load(); }}>✕</button>
            )}
            <button className="btn btn-outline-primary btn-sm text-nowrap" onClick={load} disabled={loading}>
              <i className="fas fa-sync-alt me-1"></i>Refresh
            </button>
            <button className="btn btn-warning btn-sm" onClick={onClose}>ปิด</button>
          </div>
        </div>

        {loading && <Loader />}

        {/* Body: left = two grids, right = detail */}
        <div className="d-flex" style={{ gap: 10, flex: 1, minHeight: 0 }}>
          {/* Left panel */}
          <div className="d-flex flex-column" style={{ width: "45%", minWidth: 0, gap: 8 }}>
            <div style={{ fontSize: 13 }} className="fw-bold">
              Queue <span className="text-muted fw-normal">({queue.length}) — รอส่ง{maxRows > 0 ? ` · แสดง ${maxRows} รายการล่าสุด` : ""}</span>
            </div>
            {/* Queue is usually empty - keep it short */}
            <div className="div-table-fix-header" style={{ maxHeight: 130, flexShrink: 0 }}>
              <HistoryTable
                rows={queue.map(q => ({
                  key: q.queueID, hookUrl: q.hookUrl, pk: q.pk, queueDate: q.queueDate,
                  processDateTime: q.isProcessing ? "(กำลังส่ง…)" : "", isError: false,
                }))}
                isPicked={id => isPicked("queue", id)}
                pickedStyle={pickedStyle}
                onPick={id => pick({ kind: "queue", id })}
                emptyText="ไม่มีคิวค้าง"
              />
            </div>

            <div style={{ fontSize: 13 }} className="fw-bold">
              History <span className="text-muted fw-normal">
                ({log.length}){maxRows > 0 ? ` — แสดง ${maxRows} รายการล่าสุด` : ""}
              </span>
            </div>
            <div className="div-table-fix-header" style={{ flex: 1, minHeight: 0 }}>
              <HistoryTable
                rows={log.map(l => ({
                  key: l.entryId, hookUrl: l.hookUrl, pk: l.pk, queueDate: l.queueDate,
                  processDateTime: fmtDate(l.processDateTime), isError: l.isError === true,
                }))}
                isPicked={id => isPicked("log", id)}
                pickedStyle={pickedStyle}
                onPick={id => pick({ kind: "log", id })}
                emptyText="ไม่มีประวัติ"
              />
            </div>
          </div>

          {/* Right panel */}
          <div style={{
            flex: 1, minWidth: 0, overflowY: "auto", padding: 10, borderRadius: 6,
            border: "1px solid var(--m2-line-divider-color)",
          }}>
            {!picked && <div className="text-muted text-center mt-5">เลือกรายการทางซ้ายเพื่อดูรายละเอียด</div>}
            {picked && loadingDetail && <div className="text-muted">Loading…</div>}
            {picked && detailError && <div className="text-danger">{detailError}</div>}
            {picked && detail && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    {picked.kind === "queue" && <span className="bs-badge bs-badge-secondary">Queue — รอส่ง</span>}
                    {picked.kind === "log" && (detail.isError
                      ? <span className="bs-badge bs-badge-danger">Error</span>
                      : <span className="bs-badge bs-badge-success">Success</span>)}
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={perform} disabled={performing}
                    title={picked.kind === "queue"
                      ? "ส่งคิวนี้เดี๋ยวนี้ แล้วย้ายไปไว้ใน History"
                      : "ส่งซ้ำด้วยตัวแปรเดิม กับการตั้งค่า Hook ปัจจุบัน — จะได้ History แถวใหม่"}>
                    <i className={classnames("fas me-1", performing ? "fa-spinner fa-spin" : "fa-paper-plane")}></i>
                    Perform Hook Callback
                  </button>
                </div>
                {picked.kind === "log" && (
                  <DetailSection title="Hook Param — ค่าที่ส่งไปจริง">
                    <TextOrJson text={detail.hookParam} isDark={isDark} />
                  </DetailSection>
                )}
                {picked.kind === "log" && (
                  <DetailSection title="Result — คำตอบจากปลายทาง">
                    <TextOrJson text={detail.result} isDark={isDark} />
                  </DetailSection>
                )}
                {picked.kind === "log" && (
                  <DetailSection title="Error">
                    <TextOrJson text={detail.error} isDark={isDark} danger />
                  </DetailSection>
                )}
                <DetailSection title="Variables — ตัวแปรทั้งหมดของ event">
                  <TextOrJson text={detail.variables} isDark={isDark} />
                </DetailSection>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function HistoryTable({ rows, isPicked, pickedStyle, onPick, emptyText }: {
  rows: { key: number; hookUrl?: string | null; pk?: string | null; queueDate?: string | null; processDateTime?: string | null; isError: boolean }[];
  isPicked: (id: number) => boolean;
  pickedStyle: React.CSSProperties;
  onPick: (id: number) => void;
  emptyText: string;
}) {
  return (
    <table style={{ fontSize: 12 }}>
      <thead>
        <tr>
          <th>Hook Url</th>
          <th>PK</th>
          <th style={{ whiteSpace: "nowrap" }}>Queue Date</th>
          <th style={{ whiteSpace: "nowrap" }}>Process Date</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={4} align="center" className="text-muted">{emptyText}</td></tr>}
        {rows.map(r => (
          <tr key={r.key} className={classnames("hand", { "text-danger": r.isError && !isPicked(r.key) })}
            style={isPicked(r.key) ? pickedStyle : undefined}
            onClick={() => onPick(r.key)}
            title={r.isError ? "Error" : undefined}>
            <td style={{ wordBreak: "break-all" }}>
              {r.isError && <i className="fas fa-exclamation-circle me-1"></i>}
              {r.hookUrl}
            </td>
            {/* Task ids are long - show the start, full value on hover */}
            <td title={r.pk ?? undefined} style={{ whiteSpace: "nowrap", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis" }}>
              {r.pk}
            </td>
            <td style={{ whiteSpace: "nowrap" }}>{fmtDate(r.queueDate)}</td>
            <td style={{ whiteSpace: "nowrap" }}>{r.processDateTime}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
